import type { GetServerSideProps, NextPage } from "next";
import React from "react";
import getAppSettings, { AppSettings } from "../../../lib/get-app-settings";
import authenticateViaUrlParameters from "../../../lib/authenticate-via-url-parameters";
import handleMissingUserHash from "../../../lib/handle-missing-user-hash";
import {
  THelpCenterLightLayout,
  THelpCenterStandardLayout,
  HelpCenterLayout,
} from "../../../lib/load-company-settings";
import TicketsStandardPage, {
  getServerSideProps as getStandardPageProps,
  type TTicketsStandardPageProps,
} from "./_index.standard";
import TicketsLightPage, {
  getServerSideProps as getLightPageProps,
  type TTicketsLightPageProps,
} from "./_index.light";

type TSupportPortalProps = any;

const TicketsPage: NextPage<TSupportPortalProps> = ({
  appSettings,
  ...props
}) => {
  if (appSettings.layout.startsWith("light_")) {
    return (
      <TicketsLightPage
        {...(props as TTicketsLightPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterLightLayout }
        }
      />
    );
  } else {
    return (
      <TicketsStandardPage
        {...(props as TTicketsStandardPageProps)}
        appSettings={
          appSettings as AppSettings & { layout: THelpCenterStandardLayout }
        }
      />
    );
  }
};

export default TicketsPage;

export const getServerSideProps: GetServerSideProps<
  TSupportPortalProps
> = async (context) => {
  // this is nearly always the first function
  // to be called when the user opens the help center
  // since CNAME -> is mapped to -> /[companySlug]
  const appSettings = await getAppSettings(context);

  if (!appSettings) {
    return { redirect: { statusCode: 302, destination: "/" } };
  }

  const authResponse = await authenticateViaUrlParameters(appSettings, context);
  if (authResponse) return authResponse;

  const result = await handleMissingUserHash(appSettings, context);
  if (result) return result;

  const previewLayout = context.query.preview_layout?.toString();
  if (
    previewLayout?.match(
      /^(light_sidebar|standard_(condensed|horizontal|grid))$/
    )
  ) {
    appSettings.layout = previewLayout as HelpCenterLayout;
  }

  if (appSettings.layout.startsWith("light_")) {
    return getLightPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterLightLayout }
    );
  } else {
    return getStandardPageProps(
      context,
      appSettings as AppSettings & { layout: THelpCenterStandardLayout }
    );
  }
};
